import { MealSearchRequest, MealTime } from "@/clients";
import { MealsService } from "@/clients/services/MealsService";
import { QueryKeys } from "@/constants/query-keys";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";

interface UseSearchMealsParams {
  query?: string;
  mealTime?: MealTime;
  limit?: number;
  mealTypeCategory?: string;
  includeMealTypeCategories?: string[];
  excludeMealTypeCategories?: string[];
  brandFilter?: string;
  enabled?: boolean;
}

export const useSearchMeals = ({
  query,
  mealTime,
  limit,
  mealTypeCategory,
  includeMealTypeCategories,
  excludeMealTypeCategories,
  brandFilter,
  enabled = true,
}: UseSearchMealsParams) => {
  const normalizedQuery = query?.trim() ?? "";

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      QueryKeys.MEAL_SEARCH,
      normalizedQuery,
      mealTime ?? "",
      limit ?? "",
      mealTypeCategory ?? "",
      includeMealTypeCategories?.join(",") ?? "",
      excludeMealTypeCategories?.join(",") ?? "",
      brandFilter ?? "",
    ],
    enabled: enabled && (!!mealTime || normalizedQuery.length > 0),
    queryFn: async () => {
      const user = guardGetUserFromLocalStorage();
      const requestBody: MealSearchRequest = {
        query: normalizedQuery || undefined,
        meal_time: mealTime,
        meal_type_category: mealTypeCategory,
        include_meal_type_categories: includeMealTypeCategories,
        exclude_meal_type_categories: excludeMealTypeCategories,
        brand_filter: brandFilter,
        limit,
      };

      const { data } = await MealsService.searchMealsApiV1MealsSearchPost(user.id, requestBody);
      return data;
    },
  });

  return {
    meals: data?.meals ?? [],
    totalResults: data?.total_results ?? 0,
    queryUsed: data?.query ?? null,
    filtersApplied: data?.filters_applied,
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};
